import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalTechnologyNonComplianceItemModel } from 'forms/sections/legal/technology/noncompliance/types';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import PVQLocation from 'components/Form/PVQLocation';
import { isPVQForm } from 'helpers/form';
import { systemITLocationOptions, handlingProtectedInformationDisciplinaryOptions } from 'constants/enums/informationTechnologyOptions';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';

interface Props extends Partial<LegalTechnologyNonComplianceItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  formType?: string

}

export default class NonComplianceItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalTechnologyNonComplianceItemModel>) => {
    this.props.onUpdate({
      Date: this.props.Date, // PVQOnly
      Incident: this.props.Incident, // PVQOnly
      Location: this.props.Location, // PVQOnly
      NonComplianceReason: this.props.NonComplianceReason, // PVQOnly
      NonComplianceDisciplinaryAction: this.props.NonComplianceDisciplinaryAction, // PVQOnly
      NonComplianceIncidentLocation: this.props.NonComplianceIncidentLocation, // PVQOnly
      DisciplinaryActionExplanation: this.props.DisciplinaryActionExplanation, // PVQOnly
      DisciplinaryActionOtherExplanation: this.props.DisciplinaryActionOtherExplanation, // PVQOnly
      ...queue,
    });
  };

  updateDate = (values: LegalTechnologyNonComplianceItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateIncident = (values: LegalTechnologyNonComplianceItemModel['Incident']) => {
    this.update({
      Incident: values,
    });
  };

  updateLocation = (values: LegalTechnologyNonComplianceItemModel['Location']) => {
    this.update({
      Location: values,
    });
  };

  updateNonComplianceReason = (values: LegalTechnologyNonComplianceItemModel['NonComplianceReason']) => {
    this.update({
      NonComplianceReason: values,
    });
  };

  updateDisciplinaryActionOtherExplanation = (values: LegalTechnologyNonComplianceItemModel['DisciplinaryActionOtherExplanation']) => {
    this.update({
      DisciplinaryActionOtherExplanation: values,
    });
  };

  updateDisciplinaryActionExplanation = (values: LegalTechnologyNonComplianceItemModel['DisciplinaryActionExplanation']) => {
    this.update({
      DisciplinaryActionExplanation: values,
    });
  };

  updateNonComplianceDisciplinaryAction = (values: LegalTechnologyNonComplianceItemModel['NonComplianceDisciplinaryAction']) => {
    const noncomplianceDisciplinaryAction = values?.value;
    const selected = [...((this.props.NonComplianceDisciplinaryAction || {}).values || [])];
    if (selected.includes(noncomplianceDisciplinaryAction)) {
      selected.splice(selected.indexOf(noncomplianceDisciplinaryAction), 1);
    } else {
      selected.push(noncomplianceDisciplinaryAction);
    }
    this.update({
      NonComplianceDisciplinaryAction: { values: selected },
      DisciplinaryActionOtherExplanation: selected?.includes('Other') ? this.props.DisciplinaryActionOtherExplanation : {}
    });
  };

  updateNonComplianceIncidentLocation = (values: LegalTechnologyNonComplianceItemModel['NonComplianceIncidentLocation']) => {
    const nonComplianceIncidentLocation = values?.value;
    const selected = [...((this.props.NonComplianceIncidentLocation || {}).values || [])];
    if (selected.includes(nonComplianceIncidentLocation)) {
      selected.splice(selected.indexOf(nonComplianceIncidentLocation), 1);
    } else {
      selected.push(nonComplianceIncidentLocation);
    }
    this.update({
      NonComplianceIncidentLocation: { values: selected },
    });
  };

  render() {
    const { formType } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(formType);

    return isPVQ ? (
      <div>
        <Field
          title={i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.date')}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            {...this.props.Date}
            name="Date"
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="legal-technology-noncompliance-date"
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          attribute={`${attribute}.NonComplianceIncidentLocation`}
          title={i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.accessLocation')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
        >

          <p>{i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.checkAll')}</p>
          <CheckboxGroup
            className="protected-information-access-location option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={(this.props.NonComplianceIncidentLocation || {}).values}
            attribute={`${attribute}.NonComplianceIncidentLocation`}
          >
            {systemITLocationOptions.map((s, idx) => (
              <Checkbox
                name="noncompliance-incident-location"
                label={s}
                key={`location-${idx}`}
                value={s}
                className="legal-police-noncompliance-incident-checkbox"
                onUpdate={this.updateNonComplianceIncidentLocation}
                onError={this.props.onError}
              />
            ))}
          </CheckboxGroup>
        </Field>

        {this.props.NonComplianceIncidentLocation?.values?.includes('Other') && (
          <Field
            title={i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.otherLocationExplanation')}
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Incident`}
          >
            <Textarea
              {...this.props.Incident}
              name="Incident"
              onUpdate={this.updateIncident}
              onError={this.props.onError}
              className="legal-technology-noncompliance-incident"
              required={this.props.required}
              attribute={`${attribute}.Incident`}
            />
          </Field>
        )}

        <PVQLocation
          {...this.props.Location}
          attribute={`${attribute}.Location`}
          hideInstallationNameUnknownWhenForeign
          name="Location"
          label={i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.location')}
          domesticTitle={i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.domesticTitle')}
          foreignTitle={i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.foreignTitle')}
          includeMilitaryStates
          layout={PVQLocation.ADDRESS}
          className="legal-technology-noncompliance-location"
          geocode
          addressBook="Incident"
          addressBooks={this.props.addressBooks}
          dispatch={this.props.dispatch}
          onUpdate={this.updateLocation}
          onError={this.props.onError}
          required={this.props.required}
          isPoBoxAllowed={false}
          showMilitaryInstallation
          showApoFpoDpoPostcodeWhenForeign
          showInternationalStreet={false}
        />

        <Field
          title={i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.nonComplianceReason')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.NonComplianceReason`}
        >
          <Textarea
            {...this.props.NonComplianceReason}
            name="NonComplianceReason"
            onUpdate={this.updateNonComplianceReason}
            onError={this.props.onError}
            className="legal-technology-noncompliance-access-attempt-reason"
            required={this.props.required}
            attribute={`${attribute}.NonComplianceReason`}
          />
        </Field>
        <Field
          attribute={`${attribute}.NonComplianceDisciplinaryAction`}
          title={i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.nonComplianceDisciplinaryAction')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
        >
          <p>{i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.checkAll')}</p>
          <CheckboxGroup
            className="protected-information-access-disciplinary-action option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={(this.props.NonComplianceDisciplinaryAction || {}).values}
            attribute={`${attribute}.NonComplianceDisciplinaryAction`}
          >
            {handlingProtectedInformationDisciplinaryOptions.map((s, idx) => (
              <Checkbox
                name="protected-information-noncompliance-disciplinary-location"
                label={s}
                key={`disciplinary-${idx}`}
                value={s}
                onUpdate={this.updateNonComplianceDisciplinaryAction}
                onError={this.props.onError}
              />
            ))}
          </CheckboxGroup>
        </Field>
        {this.props.NonComplianceDisciplinaryAction?.values?.includes('Other') && (
          <Field
            title={i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.disciplinaryActionOtherExplanation')}
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.DisciplinaryActionOtherExplanation`}
          >
            <Textarea
              {...this.props.DisciplinaryActionOtherExplanation}
              name="DisciplinaryActionOtherExplanation"
              onUpdate={this.updateDisciplinaryActionOtherExplanation}
              onError={this.props.onError}
              className="legal-technology-noncompliance-access-discipinary-action"
              required={this.props.required}
              attribute={`${attribute}.DisciplinaryActionOtherExplanation`}
            />
          </Field>
        )}
        <Field
          title={i18n.t('legal.pvq.protectedInformation.subsection.noncompliance.disciplinaryActionExplanation')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DisciplinaryActionExplanation`}
        >
          <Textarea
            {...this.props.DisciplinaryActionExplanation}
            name="DisciplinaryActionExplanation"
            onUpdate={this.updateDisciplinaryActionExplanation}
            onError={this.props.onError}
            className="legal-technology-noncompliance-access-discipinary-action-explanation"
            required={this.props.required}
            attribute={`${attribute}.DisciplinaryActionExplanation`}
          />
        </Field>
      </div>
    ) : (
      {/* Not Applicable - PVQ Only */ }
    );
  }
}

NonComplianceItem.defaultProps = {
  Date: {},
  Incident: {},
  Location: {},
  NonComplianceReason: {}, // PVQOnly
  NonComplianceDisciplinaryAction: {}, // PVQOnly
  NonComplianceIncidentLocation: {}, // PVQOnly
  DisciplinaryActionExplanation: {}, // PVQOnly
  DisciplinaryActionOtherExplanation: {}, // PVQOnly
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode) => arr,
};
